<template>
  <div>
    <div class="featured" :style="getBackgroundColor" @click="openCourse">
      <h1 class="theme-name">{{ props.themeName }}</h1>
      <swiper
        :slides-per-view="'auto'"
        :space-between="25"
        :centeredSlides="true"
        :modules="[Pagination]"
        :pagination="{ clickable: true }"
        @slideChange="onSlideChange"
      >
        <swiper-slide v-for="data in featuredSliderData" :key="data.id">
          <FeaturedPodcast
            :featured-podcast="data"
            :display-info="displayInfo"
          />
        </swiper-slide>
      </swiper>
      <h2 class="featured-title">{{ activeFeaturedPodcast.main_title }}</h2>
      <p class="featured-description is-displaying-three-lines-text">
        {{ activeFeaturedPodcast.description }}
      </p>
      <span class="featured-company-name">
        {{ activeFeaturedPodcast.company_name }}
      </span>

      <!-- Actions -->
      <div class="featured-actions">
        <div
          class="add-icon-with-fireworks-animation-wrapper"
          @click.stop="handleAddClick"
        >
          <ion-icon
            :icon="addIcon"
            size="large"
            class="clickable-icon"
          ></ion-icon>

          <!-- Animation -->
          <template v-if="showAddAnimation">
            <div v-for="particle in 12" :key="particle" class="particle" />
          </template>
        </div>

        <ion-icon :icon="playCircle" size="large"></ion-icon>
      </div>
    </div>
  </div>
</template>

<script setup>
// NOTE: This component is used for the featured course with a theme size "full"

import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { ref, computed } from 'vue';
import { TRACKING_FEATURED_COURSE_VIEW } from '@/config/constants';
import { getSmallestSizeImage } from '@/helpers/functions';

import { addCircleOutline, checkmarkCircle, playCircle } from 'ionicons/icons';
import { IonIcon } from '@ionic/vue';

import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

import useBackgroundColor from '@/composables/useBackgroundColor';
import useTracking from '@/composables/useTracking';
import useFavorites from '@/composables/useFavorites';

import FeaturedPodcast from '@/components/common/FeaturedPodcast.vue';

defineOptions({
  name: 'FeaturedFullSlider',
});

const props = defineProps({
  featuredSliderData: {
    type: Array,
    required: true,
  },
  themeName: {
    type: String,
    required: true,
  },
  themeSize: {
    type: String,
    required: true,
  },
});

const displayInfo = props.themeName !== 'Geaccrediteerde podcasts';

const router = useRouter();
const store = useStore();
const { trackThis } = useTracking();

const openCourse = async () => {
  const { id, course_id, entity_type } = activeFeaturedPodcast.value;

  trackThis(TRACKING_FEATURED_COURSE_VIEW, {}, id);
  store.commit('course/setCourseTrackingState', false);

  if (['series', 'season'].includes(entity_type)) {
    router.push(`/course/${course_id}`);
  } else if (entity_type === 'episode') {
    const course = await store.dispatch('course/setCourseData', course_id);

    if (!course.streams.length) {
      router.push(`/course/${course_id}`);
      return;
    }

    store.dispatch('player/openPlayer', {
      course,
      company: course.company,
      playlist: course.streams,
      startAt: 0,
    });
  }
};

const activeFeaturedPodcast = ref(props.featuredSliderData[0]);
function onSlideChange(event) {
  activeFeaturedPodcast.value = props.featuredSliderData[event.activeIndex];
}

const { getLightColor } = useBackgroundColor();
const getBackgroundColor = computed(() => {
  const image = getSmallestSizeImage(activeFeaturedPodcast.value.media);
  const palette = image?.vibrant_palette_json;

  if (!palette) {
    return {
      '--background': '#dfe0e2',
    };
  }

  return {
    'background-color': getLightColor(palette),
  };
});

const { isFavorite, toggleFavourite } = useFavorites();

const isAdded = computed(() =>
  isFavorite(
    activeFeaturedPodcast.value.course_id,
    '',
    activeFeaturedPodcast.value.entity_type,
  ),
);

const addIcon = computed(() =>
  isAdded.value ? checkmarkCircle : addCircleOutline,
);

const showAddAnimation = ref(false);
async function handleAddClick() {
  try {
    const wasAdded = isAdded.value;

    await toggleFavourite(
      activeFeaturedPodcast.value.course_id,
      '',
      activeFeaturedPodcast.value.entity_type,
    );

    if (!wasAdded) {
      showAddAnimation.value = true;
      setTimeout(() => {
        showAddAnimation.value = false;
      }, 2000);
    }
  } catch (error) {
    console.error('Error toggling favorite:', error);
  }
}
</script>

<style scoped lang="scss">
.featured {
  background-color: rgb(77, 76, 76);
  border-radius: 25px;

  display: flex;
  flex-direction: column;

  .swiper {
    width: 100%;
    padding-bottom: 30px;

    :deep(.swiper-pagination-bullet) {
      width: 7px;
      height: 7px;
      margin: 0 3px;
    }

    :deep(.swiper-pagination-bullet-active) {
      background: black;
    }
  }

  .swiper-slide {
    width: auto;
    text-align: left;
  }

  .theme-name {
    color: black;
    font-size: 13px;
    font-weight: 700;
    padding: 15px 20px;
  }

  &-title {
    color: black;
    font-size: 21px;
    font-weight: 700;
    padding: 0 20px;
  }

  &-description {
    color: black;
    font-size: 11px;
    font-weight: 700;
    padding: 5px 20px;
    max-height: 3.8em;
  }

  &-company-name {
    color: #5e5e5e;
    font-size: 11px;
    font-weight: 700;
    padding: 5px 20px;
  }

  &-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 15px 5px 15px;
    margin-top: auto;
    color: black;
  }
}
</style>
